import * as React from 'react'

import { NavItem, NavMain } from '@/components/nav-main'

import DashboardIcon from '@/assets/icons/DashboardIcon'
import FinanceIcon from '@/assets/icons/FinanceIcon'
import IntegrationsIcon from '@/assets/icons/IntegrationsIcon'
import LogoGlobal from '@/assets/icons/LogoGlobal'
import MarketinIcon from '@/assets/icons/MarketinIcon'
import MembersIcon from '@/assets/icons/MembersIcon'
import MousePointer from '@/assets/icons/MousePointer'
import Mundpay from '@/assets/icons/Mundpay'
import ReportsIcon from '@/assets/icons/ReportsIcon'
import SalesIcon from '@/assets/icons/SalesIcon'
import TagIcon from '@/assets/icons/TagIcon'
import { useAuth } from '@/auth/Auth'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { getIntegrationsForSidebar } from '@/utils/integrations'
import {
  Globe,
  History,
  MessageCircleQuestionIcon,
  RefreshCw,
  Ship,
  Store,
  Truck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { isMarketplaceEnabled } from '@/utils/isMarketplaceEnabled'
import { usePermissions } from '@/hooks/usePermissions'
import { useToast } from '@/hooks/use-toast'
import { MembersAreaService } from '@/services/members-area.service'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation(['routes', 'common', 'integration'])
  const { authData } = useAuth()
  const { hasPermission, hasAnyPermission, isPrimary } = usePermissions()
  const { toast } = useToast()
  const [isOpeningMemberArea, setIsOpeningMemberArea] = React.useState(false)

  const integrationSubItems = getIntegrationsForSidebar()

  // O link SSO da MemberClass só existe depois da resposta da API, e abrir a aba
  // no retorno do await cai no bloqueador de pop-up. Por isso a aba nasce no
  // clique e só recebe a URL quando o link chega. Sem 'noopener' nas features,
  // senão o window.open devolve null; o vínculo é cortado com tab.opener = null.
  const openMemberArea = async () => {
    if (isOpeningMemberArea) return

    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    setIsOpeningMemberArea(true)

    try {
      const { link_to_login } = await MembersAreaService.authenticateProducer()

      if (tab) {
        tab.location.href = link_to_login
      } else {
        window.location.href = link_to_login
      }
    } catch {
      tab?.close()
      toast({
        title: t('common:member_area_access_error_title'),
        description: t('common:member_area_access_error_desc'),
        variant: 'destructive',
      })
    } finally {
      setIsOpeningMemberArea(false)
    }
  }

  const openExternalMarketplace = () => {
    const areaClientBaseUrl = (
      (import.meta.env.VITE_AREA_CLIENT_URL as string | undefined) ||
      'https://orders.mundpay.com'
    ).replace(/\/$/, '')
    window.open(`${areaClientBaseUrl}/marketplace`, '_blank', 'noopener,noreferrer')
  }

  const data: { navMain: NavItem[] } = {
    navMain: [
      {
        title: t('dashboard'),
        url: '/dashboard',
        icon: <DashboardIcon />,
        disabled: false,
      },
      {
        title: t('common:marketing'),
        icon: <MarketinIcon />,
        url: '/marketplace',
        disabled: !isMarketplaceEnabled,
        requiredPermission: 'marketplace.view_marketplace',
        subItems: [
          {
            title: t('routes:marketplace_internal'),
            url: '/marketplace',
            icon: <Store size={16} />,
            requiredPermission: 'marketplace.view_marketplace',
          },
          {
            title: t('routes:marketplace_external'),
            url: '',
            icon: <Globe size={16} />,
            requiredPermission: 'marketplace.view_marketplace',
            onClick: openExternalMarketplace,
          },
        ],
      },
      {
        title: t('products'),
        icon: <TagIcon />,
        url: '/products',
        disabled: false,
        requiredPermissions: [
          'products.view_products',
          'products.view_products_by_role',
          'shipping.view_shipping_configurations',
        ],
        subItems: [
          {
            title: t('routes:products'),
            url: '/products',
            icon: <TagIcon />,
            requiredPermissions: [
              'products.view_products',
              'products.view_products_by_role',
            ],
          },
          {
            title: t('routes:shipping'),
            url: '/products/shipments',
            icon: <Ship size={16} />,
            requiredPermission: 'shipping.view_shipping_configurations',
          },
        ],
      },
      {
        title: t('common:access_member_area'),
        icon: <MembersIcon />,
        url: '',
        disabled: false,
        hidden: !authData?.member_area_enabled,
        requiredPermission: 'view_member_areas',
        onClick: openMemberArea,
      },
      {
        title: t('sales'),
        url: '/sales',
        icon: <SalesIcon />,
        disabled: false,
        requiredPermissions: [
          'metrics.view_sale_data',
          'order_details.view_order_details',
          'orders.view_orders_list',
        ],
        subItems: [
          {
            title: t('routes:sales_history'),
            url: '/sales',
            icon: <History size={16} />,
            requiredPermissions: [
              'metrics.view_sale_data',
              'order_details.view_order_details',
            ],
          },
          {
            title: t('routes:shipments'),
            url: '/sales/orders',
            icon: <Truck size={16} />,
            requiredPermission: 'orders.view_orders_list',
          },
        ],
      },
      ...(authData?.can_offer_recurring
        ? [
            {
              title: t('routes:signatures'),
              url: '/signatures',
              icon: <RefreshCw size={16} />,
              disabled: false,
              requiredPermissions: ['metrics.view_sale_data'],
            },
          ]
        : []),
      {
        title: t('finance'),
        url: '/financial',
        icon: <FinanceIcon />,
        disabled: false,
        requiredPermissions: [
          'wallet.view_available_balance',
          'wallet.view_balance_to_release',
          'wallet.view_retention_balance',
          'wallet.view_total_withdrawn',
          'financial.view_withdrawals_list',
          'financial.view_account_statement',
          'financial.view_balance_forecast',
          'financial.view_transactions',
          'financial.view_fees_and_terms',
        ],
      },
      {
        title: t('integrations'),
        url: '/integrations/configurations',
        icon: <IntegrationsIcon />,
        disabled: false,
        requiredPermissions: [
          'integrations.view_integrations',
          'integrations.view_webhook_history',
        ],
      },
      {
        title: t('reports'),
        url: '/reports',
        icon: <ReportsIcon />,
        disabled: false,
        requiredPermissions: [
          'reports.view_cart_abandonment',
          'reports.view_financial_analysis',
          'reports.view_utm_panel',
          'reports.view_analytics',
        ],
      },
      {
        title: t('win'),
        url: '/refer-and-earn',
        icon: <MousePointer />,
        disabled: false,
        requiredPermissions: [
          'referral.view_referral_program',
          'referral.view_referral_link',
        ],
      },
    ],
  }

  const visibleNav = data.navMain.filter((item) => {
    if (item.primaryOnly && !isPrimary) return false
    if (isPrimary) return true
    if (item.requiredPermissions?.length)
      return hasAnyPermission(item.requiredPermissions)
    if (item.requiredPermission) return hasPermission(item.requiredPermission)
    return true
  })

  const handleWhatsAppClick = () => {
    const phoneNumber = '5521988294968'
    const message = t('offers:whatsapp_message', {
      name: authData?.name || '',
      email: authData?.email || '',
    })
    const encodedMessage = encodeURIComponent(message)
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`
    window.open(whatsappUrl, '_blank')
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="flex items-center flex-row">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Link
                to="/dashboard"
                className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground"
              >
                <LogoGlobal />
              </Link>
              <Link
                to="/dashboard"
                className="grid flex-1 text-left text-sm leading-tight"
              >
                <Mundpay />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="gap-3">
        <NavMain items={visibleNav} />
      </SidebarContent>
      <SidebarFooter className="p-2">
        <button
          onClick={handleWhatsAppClick}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-colorText/70 transition-all hover:bg-elevated hover:text-colorText"
        >
          <MessageCircleQuestionIcon className="h-5 w-5 shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden">
            {t('common:support')}
          </span>
        </button>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
